/**
 * Modal
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v1.8.0
 * 2024-04-25
 */
class Modal
{

    #settings = {
        callers: 'a[rel]',
        callees: '*[data-modal]',
        relations: {
            rel: 'data-modal',
        },
        ignore: '#consent-manager',
        closer: 'button:not([type="submit"]):not([name])',
        appendTo: null,
        appendToBody: false,
        wrapContent: true,
        debug: false,
        autoplayVideo: true,
        onReady: (callee) => {},
        onShow: (callee) => {},
        onDismiss: (callee) => {}
    }

    /**
     * Modal constructor
     *
     * @param options
     */
    constructor(options)
    {
        this.#settings = {...this.#settings, ...options}
        if (String === this.#settings.ignore.constructor) {
            this.#settings.ignore = document.querySelectorAll(this.#settings.ignore)
        }
        this.#initCallers()
        this.#initCallees()
    }

    /**
     * Initialize all modal-calling elements at boot
     */
    #initCallers()
    {
        const callers = document.querySelectorAll(this.#settings.callers)
        if (!callers) {
            this.#settings.debug && console.warn('[Modal]', 'No button / callers found')
            return
        }
        callers.forEach(caller => {
            this.#initCaller(caller)
        })
    }

    /**
     * Initialize one modal-calling element
     *
     * @param {HTMLElement} caller
     * @param {HTMLElement|null} callee
     */
    #initCaller(caller, callee = null)
    {
        let relatedCallee
        if (callee) {
            relatedCallee = callee
        }
        else {
            Object.keys(this.#settings.relations).forEach(callerAttribute => {
                const calleeAttribute = this.#settings.relations[callerAttribute]
                if (!caller.hasAttribute(callerAttribute)) {
                    if (!caller.href) {
                        console.error('[Modal]', 'Tried to initialize caller but there\'s no related callee available.', caller)
                        return
                    }
                    relatedCallee = document.querySelector(`[${calleeAttribute}="${caller.href}"]`) || this.#createCallee(caller.href, calleeAttribute)
                }
                else {
                    const calleeIdentifier = caller.getAttribute(callerAttribute)
                    relatedCallee = document.querySelector(`[${calleeAttribute}="${calleeIdentifier}"]`)
                    if (0 <= Object.values(this.#settings.ignore).indexOf(relatedCallee)) {
                        relatedCallee = null
                    }
                }
            })
        }
        if (!relatedCallee) {
            this.#settings.debug && console.warn('[Modal]', 'Caller could not be initialised for caller', caller)
            return
        }
        this.#initCallee(relatedCallee)
        caller.classList.add('modal__caller')
        caller.addEventListener('click', (e) => {
            e.preventDefault()
            this.show(relatedCallee)
        })
        this.#settings.debug && console.log('[Modal]', 'Initialised caller related to callee', caller, relatedCallee)
    }

    /**
     * Create modal / callee
     *
     * @param {String} url
     * @param {String} setAttribute
     * @returns {HTMLElement} callee
     */
    #createCallee(url, setAttribute)
    {
        const urlType = (new RegExp(/.mp4/i)).test(url) ? Modal.URL_TYPE_FILE : Modal.URL_TYPE_SITE // TODO: very rudimentary
        const callee = document.createElement('aside')
        callee.classList.add('modal')
        callee.setAttribute(setAttribute, url)
        if (Modal.URL_TYPE_SITE === urlType) {
            const iframe = document.createElement('iframe')
            iframe.src = url
            iframe.classList.add('modal__iframe')
            callee.append(iframe)
        }
        else if (Modal.URL_TYPE_FILE === urlType) {
            // TODO: also support images
            const video = document.createElement('video')
            const source = document.createElement('source')
            source.src = url
            video.append(source)
            video.autoplay = false
            video.controls = true
            video.playsInline = true
            video.classList.add('modal__video')
            callee.append(video)
        }
        document.body.append(callee)
        this.#initCallee(callee)
        return callee
    }

    /**
     * Initialize all modals / calless at boot
     */
    #initCallees()
    {
        const callees = document.querySelectorAll(this.#settings.callees)
        if (!callees) {
            this.#settings.debug && console.warn('[Modal]', 'No modals / callees found')
            return
        }
        callees.forEach(callee => {
            this.#initCallee(callee)
        })
    }

    /**
     * Initialize one modal / callee
     *
     * @param {HTMLElement} callee
     */
    #initCallee(callee)
    {
        this.#settings.debug && console.warn('[Modal]', 'Initialised modal / callee', callee)
        if (this.#settings.appendTo) {
            this.#settings.appendTo.append(callee)
        }
        else if (true === this.#settings.appendToBody) {
            document.body.append(callee)
        }
        let wrapper
        if (true === this.#settings.wrapContent && !callee.querySelector('.modal__content')) {
            wrapper = document.createElement('div')
            wrapper.classList.add('modal__content')
            if (callee.children.length) {
                Object.values(callee.children).forEach(child => {
                    wrapper.append(child)
                })
            }
            else {
                wrapper.textContent = callee.textContent
                callee.textContent = ''
            }
            callee.append(wrapper)
        }
        callee.classList.add('modal')
        let closer = callee.querySelector(this.#settings.closer)
        if (!closer) {
            closer = document.createElement('button')
            closer.textContent = '×'
            if (wrapper) {
                wrapper.append(closer)
            }
            else {
                callee.append(closer)
            }
        }
        closer.classList.add('modal__closer')
        closer.addEventListener('click', (e) => {
            e.preventDefault()
            this.dismiss(callee)
        })
        callee.addEventListener('click', (e) => {
            if (e.target !== callee) {
                return
            }
            this.dismiss(callee)
        })
        window.addEventListener('resize', () => {
            // TODO: Implement setTimeout()
            if (callee.clientHeight < callee.scrollHeight) {
                callee.classList.add('-overflows')
                return
            }
            callee.classList.remove('-overflows')
        })
    }

    /**
     * Show a modal
     *
     * @param callee
     */
    show(callee)
    {
        callee.classList.add('-ready')
        if (this.#settings.onReady && Function === this.#settings.onReady.constructor) {
            this.#settings.onReady(callee)
        }
        document.body.classList.add('-modal-ready')
        setTimeout(() => {
            callee.classList.add('-visible')
            if (this.#settings.onShow && Function === this.#settings.onShow.constructor) {
                this.#settings.onShow(callee)
            }
            if (this.#settings.autoplayVideo) {
                callee.querySelector('video')?.play()
            }
        }, 10)
    }

    /**
     * Dismiss a modal
     *
     * @param callee
     */
    dismiss(callee)
    {
        if (this.#settings.autoplayVideo) {
            callee.querySelector('video')?.pause()
        }
        callee.classList.remove('-visible')
        setTimeout(() => {
            callee.classList.remove('-ready')
            document.body.classList.remove('-modal-ready')
            if (this.#settings.onDismiss && Function === this.#settings.onDismiss.constructor) {
                this.#settings.onDismiss(callee)
            }
        }, 500)
    }

    /**
     * Add a caller-callee pair after boot
     *
     * @param {String|HTMLElement} caller
     * @param {String|HTMLElement|null} callee
     */
    add(caller, callee = null)
    {
        if (String === caller.constructor) {
            caller = document.querySelector(caller)
            if (!caller) {
                this.#settings.debug && console.warn(caller)
                return
            }
        }
        if (null !== callee && String === callee.constructor) {
            callee = document.querySelector(callee) || document.querySelector(`[data-modal*="${callee}"]`)
            if (!callee) {
                this.#settings.debug && console.warn(callee)
                return
            }
        }
        this.#initCaller(caller, callee)
    }

    /**
     * Alias method for add()
     *
     * @param {String|HTMLElement} caller
     * @param {String|HTMLElement|null} callee
     */
    addCaller(caller, callee = null)
    {
        this.add(caller, callee)
    }

    /**
     * Add relation
     *
     * @param {String} callerAttribute
     * @param {String} calleeAttribute
     */
    addRelation(callerAttribute, calleeAttribute)
    {
        if (0 <= Object.keys(this.#settings.relations).indexOf(callerAttribute)) {
            this.setRelation(callerAttribute, calleeAttribute)
            return
        }
        this.#settings.relations[callerAttribute] = calleeAttribute
    }

    /**
     * Set (overwrite) relation
     *
     * @param {String} callerAttribute
     * @param {String} calleeAttribute
     */
    setRelation(callerAttribute, calleeAttribute)
    {
        if (0 > Object.keys(this.#settings.relations).indexOf(callerAttribute)) {
            return
        }
        this.#settings.relations[callerAttribute] = calleeAttribute
        this.#settings.callees += `,*[${calleeAttribute}]`
    }

    /**
     * Create a popup
     *
     * @param {String|HTMLElement} callee
     * @param {int} expires
     * @param {int} delay
     */
    popup(callee, expires = 24, delay = 2000)
    {
        if (String === callee.constructor) {
            callee = document.getElementById(callee) || document.querySelector(callee)
        }
        if (!callee) {
            this.#settings.debug && console.warn('[Modal]', 'popup()', 'Cannot initialize a popup since the element does not exist')
            return
        }
        this.#initCallee(callee)
        const popupStorageId = 'Modal.popup' // TODO: what about multiple popups?
        const now = Math.round((new Date).getTime() / 1000)
        let allow = true
        if (
            expires &&
            'undefined' !== typeof localStorage.getItem(popupStorageId) &&
            now < parseInt(localStorage.getItem(popupStorageId))
        ) {
            allow = false
        }
        allow && setTimeout(() => {
            this.show(callee)
            localStorage.setItem(popupStorageId, (now + (24 * 3600)))
        }, delay)
    }

    static URL_TYPE_FILE = 'file'
    static URL_TYPE_SITE = 'site'

}
export default Modal
